import { NoVncMessages, NoVncRfb } from './novnc-loader';
import { Size } from './screen-rotation';

/** The choices the page offers for the terminal's screen, from the lightest on the link to the sharpest. */
export const SCREEN_PRESETS = ['light', 'balanced', 'sharp'] as const;
export type ScreenPreset = (typeof SCREEN_PRESETS)[number];
export const DEFAULT_SCREEN_PRESET: ScreenPreset = 'balanced';

/** What the page asks the terminal for (agronave-app `remotesupport/PROTOCOL.md`, "What the viewer controls"). */
export interface ScreenSettings {
  /** JPEG quality level, 0 to 9: level L is JPEG quality (L + 1) × 10. */
  qualityLevel: number;
  /** zlib level, 0 to 9, of what goes without loss: it trades the terminal's processor for bandwidth. */
  compressionLevel: number;
  /** The share of each side of the terminal's screen the picture is sent at, from 1/8 to 1. */
  resolution: number;
}

/**
 * The settings of each preset. `balanced` is the contract's advice: quality level 4 and compression
 * 2 at the screen's own size. `light` is for slow links (half of each side, so a quarter of the
 * pixels), `sharp` for small text on a good one. Changed areas and continuous updates are on for
 * every preset: they are the terminal's default and noVNC asks for them by itself.
 */
export const SCREEN_SETTINGS: Record<ScreenPreset, ScreenSettings> = {
  light: { qualityLevel: 2, compressionLevel: 6, resolution: 0.5 },
  balanced: { qualityLevel: 4, compressionLevel: 2, resolution: 1 },
  sharp: { qualityLevel: 8, compressionLevel: 2, resolution: 1 },
};

/** How often the page looks whether the terminal can be asked for a size: it says so with its first update. */
const RESIZE_CHECK_MS = 250;
/** Checks before giving up: a terminal whose software predates resolutions never says it can. */
const MAX_RESIZE_CHECKS = 40;

/**
 * noVNC 1.7.0's state behind SetDesktopSize, which it has no API for (`resizeSession` asks for the
 * container's size, which may be larger than the terminal's screen). `@novnc/novnc` is pinned in
 * package.json; agres-remote's screen page reads the same fields.
 */
interface NoVncResizeState {
  _fbWidth: number;
  _fbHeight: number;
  /** Set by the terminal's first ExtendedDesktopSize rectangle. */
  _supportsSetDesktopSize: boolean;
  _screenID: number;
  _screenFlags: number;
  _sock: unknown;
}

/** A fraction of the screen's size, never below one pixel. */
export function sizeAt(screen: Size, resolution: number): Size {
  return {
    width: Math.max(1, Math.round(screen.width * resolution)),
    height: Math.max(1, Math.round(screen.height * resolution)),
  };
}

/**
 * Asks the terminal for the quality, compression and resolution of one `vnc` channel, and asks
 * again when the technician picks another preset.
 *
 * @remarks
 * - Quality and compression are noVNC settings: it sends them with its encodings, and again when
 *   they change while connected.
 * - The resolution is SetDesktopSize, which the terminal takes only after its first update said it
 *   can (ExtendedDesktopSize). The screen's own size is the channel's first framebuffer, and each
 *   resolution is a fraction of it, so the aspect never changes and no black strips show.
 * - Older terminals ignore compression and never say they take a size: the preset then changes the
 *   quality only.
 */
export class ScreenQuality {
  private settings: ScreenSettings;
  private connected = false;
  private screen: Size | null = null;
  private asked: Size | null = null;
  private checks = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly onConnect = () => {
    this.connected = true;
    this.askForResolution();
  };
  private readonly onDisconnect = () => this.dispose();

  constructor(
    private readonly rfb: NoVncRfb,
    private readonly messages: NoVncMessages,
    settings: ScreenSettings,
  ) {
    this.settings = settings;
    rfb.qualityLevel = settings.qualityLevel;
    rfb.compressionLevel = settings.compressionLevel;
    rfb.addEventListener('connect', this.onConnect);
    rfb.addEventListener('disconnect', this.onDisconnect);
  }

  /** Switches to other settings at once. */
  apply(settings: ScreenSettings): void {
    this.settings = settings;
    this.rfb.qualityLevel = settings.qualityLevel;
    this.rfb.compressionLevel = settings.compressionLevel;
    this.askForResolution();
  }

  dispose(): void {
    this.connected = false;
    this.stopChecking();
    this.rfb.removeEventListener('connect', this.onConnect);
    this.rfb.removeEventListener('disconnect', this.onDisconnect);
  }

  private askForResolution(): void {
    if (!this.connected) {
      return;
    }
    this.stopChecking();
    if (!this.tryAsk()) {
      this.checks = 0;
      this.timer = setInterval(() => this.check(), RESIZE_CHECK_MS);
    }
  }

  private check(): void {
    this.checks += 1;
    if (this.tryAsk() || this.checks >= MAX_RESIZE_CHECKS) {
      this.stopChecking();
    }
  }

  /** True once there is nothing left to wait for: the size is asked for, or already right. */
  private tryAsk(): boolean {
    const state = this.rfb as unknown as NoVncResizeState;
    if (!state._supportsSetDesktopSize || !state._fbWidth || !state._fbHeight) {
      return false;
    }
    if (!this.screen) {
      this.screen = { width: state._fbWidth, height: state._fbHeight };
    }
    const size = sizeAt(this.screen, this.settings.resolution);
    if (sameSize(size, this.asked)) {
      return true;
    }
    this.asked = size;
    if (sameSize(size, { width: state._fbWidth, height: state._fbHeight })) {
      return true;
    }
    this.messages.setDesktopSize(
      state._sock,
      size.width,
      size.height,
      state._screenID,
      state._screenFlags,
    );
    return true;
  }

  private stopChecking(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

function sameSize(a: Size, b: Size | null): boolean {
  return Boolean(b) && a.width === b.width && a.height === b.height;
}
